import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { getFinancialDashboard } from "api/financial";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import EmptyState from "components/bento/EmptyState";
import { TrendBadge } from "components/bento/KpiTile";
import {
  MONO, TEXT_DARK, TEXT_BODY, TEXT_SUBTLE,
  BG_WHITE, BG_PURPLE_TINT, BG_PURPLE_GRADIENT,
  BORDER,
  PURPLE_MAIN, PURPLE_LIGHT, PURPLE_DARK, PURPLE_DARKER,
  SUCCESS_BG, ERROR_BG,
  cardLabelSx,
} from "components/bento/bentoTokens";

const CURRENCIES = ["THB", "LAK", "USD"];
const CUR_SYM = { THB: "฿", LAK: "₭", USD: "$" };
const CUR_BAR_COLOR = { THB: PURPLE_MAIN, LAK: "#a78bfa", USD: "#e9d5ff" };
const initials = (name) => {
  const p = (name || "?").trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : (name || "?").slice(0, 2)).toUpperCase();
};
const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// Build an SVG polyline path for a sparkline from a list of values.
function sparkPath(values, w = 120, h = 32, pad = 4) {
  if (!values || values.length < 2) return "";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = w / (values.length - 1);
  return values
    .map((v, i) => {
      const x = i * stepX;
      const y = h - pad - ((v - min) / range) * (h - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
}

// ── Branch cash on hand: per-branch THB-equivalent total (real exchange rate,
// computed server-side) + a segmented bar showing each currency's share ────────
function BranchCashCard({ branchCash, branchTotalTHB, navigate }) {
  const { t } = useTranslation();
  const list = branchCash || [];
  return (
    <BentoCard
      title={t("financial.branchCash")}
      headerRight={
        <Box sx={{ fontFamily: MONO, fontWeight: 600, fontSize: 16, color: PURPLE_DARK, background: BG_PURPLE_TINT, padding: "5px 14px", borderRadius: "999px", whiteSpace: "nowrap" }}>
          ฿{fmt(branchTotalTHB)}
        </Box>
      }
      bodySx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,1fr)" }, gap: "20px", p: "22px 24px" }}
    >
      {list.length === 0 ? (
        <EmptyState message={t("financial.noData")} sx={{ gridColumn: "1 / -1" }} />
      ) : list.map((b) => (
        <Box
          key={b.stationId}
          onClick={() => navigate(`/financial/branch-cash/${encodeURIComponent(b.stationId)}`)}
          sx={{ border: `1px solid ${BORDER}`, borderRadius: "18px", p: "20px 22px", background: BG_PURPLE_GRADIENT, cursor: "pointer", transition: "filter .15s", "&:hover": { filter: "brightness(.98)" } }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
              <Box sx={{ width: 38, height: 38, borderRadius: "11px", background: `linear-gradient(135deg,${PURPLE_LIGHT},${PURPLE_DARKER})`, color: "#fff", fontFamily: MONO, fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                {initials(b.stationName)}
              </Box>
              <Box sx={{ fontSize: 15, fontWeight: 700, color: TEXT_BODY }}>{b.stationName}</Box>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Box sx={cardLabelSx}>{t("financial.totalThb")}</Box>
              <Box sx={{ fontFamily: MONO, fontSize: 19, fontWeight: 600, color: TEXT_DARK, mt: "1px" }}>฿{fmt(b.totalTHB)}</Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", height: "11px", borderRadius: "6px", overflow: "hidden", mt: "18px", gap: "2px" }}>
            {CURRENCIES.map((cur) => {
              const pct = b[`${cur.toLowerCase()}Pct`] || 0;
              return <Box key={cur} sx={{ height: "100%", background: CUR_BAR_COLOR[cur], width: `${pct}%`, minWidth: pct > 0 ? "2px" : 0 }} />;
            })}
          </Box>

          <Box sx={{ display: "flex", gap: "14px", mt: "16px" }}>
            {CURRENCIES.map((cur) => (
              <Box key={cur} sx={{ flex: 1, display: "flex", alignItems: "center", gap: "8px" }}>
                <Box component="span" sx={{ width: 9, height: 9, borderRadius: "3px", background: CUR_BAR_COLOR[cur], flex: "none" }} />
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 10, color: TEXT_SUBTLE, fontWeight: 700, letterSpacing: ".06em" }}>{cur}</Box>
                  <Box sx={{ fontFamily: MONO, fontSize: "13.5px", fontWeight: 600, color: TEXT_BODY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {CUR_SYM[cur]}{fmt(b[cur])}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </BentoCard>
  );
}

// ── Outstanding fee by station (ranked, progress bars, 2-col on desktop) ───────
function OutstandingCard({ outstanding, total, navigate }) {
  const { t } = useTranslation();
  const maxFee = Math.max(...outstanding.map((o) => o.totalShippingFee), 1);
  return (
    <BentoCard
      title={t("financial.outstandingByStation")}
      headerRight={
        <Box sx={{ fontFamily: MONO, fontWeight: 600, fontSize: 16, color: PURPLE_DARK, background: BG_PURPLE_TINT, padding: "5px 14px", borderRadius: "999px", whiteSpace: "nowrap" }}>
          ฿{fmt(total)}
        </Box>
      }
    >
      {outstanding.length === 0 ? (
        <EmptyState message={t("financial.noData")} />
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,1fr)" } }}>
          {outstanding.map((o, i) => (
            <Box
              key={o.station}
              onClick={() => navigate(`/financial/outstanding/${encodeURIComponent(o.station)}`)}
              sx={{ display: "flex", alignItems: "center", gap: "16px", p: "13px 24px", borderTop: i < 2 ? "none" : "1px solid #f4f2f8", cursor: "pointer", "&:hover": { background: "#faf9fc" } }}
            >
              <Box sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color: "#b9b4c4", width: 18 }}>{String(i + 1).padStart(2, "0")}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px" }}>
                  <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: TEXT_BODY }}>{o.stationName}</Box>
                  <Box sx={{ fontFamily: MONO, fontSize: 14, fontWeight: 600, color: TEXT_DARK }}>฿{fmt(o.totalShippingFee)}</Box>
                </Box>
                <Box sx={{ height: 6, borderRadius: "3px", background: "#f0eef5", mt: "7px", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", borderRadius: "3px", background: `linear-gradient(90deg,${PURPLE_LIGHT},${PURPLE_MAIN})`, width: `${Math.max((o.totalShippingFee / maxFee) * 100, 3)}%` }} />
                </Box>
              </Box>
              <Box sx={{ fontFamily: MONO, fontSize: "11.5px", color: "#aaa4b6", width: 44, textAlign: "right", flex: "none" }}>{fmt(o.count)}</Box>
            </Box>
          ))}
        </Box>
      )}
    </BentoCard>
  );
}

// ── Customer wallet: net + clickable liability/receivable pills, per currency ──
function WalletCards({ wallet, navigate }) {
  const { t } = useTranslation();
  if (!wallet) return null;
  const go = (cur, sign) => navigate(`/financial/wallets?currency=${cur}&sign=${sign}`);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" }, gap: "20px" }}>
      {CURRENCIES.map((cur) => {
        const c = wallet[cur] || {};
        const pos = c.positive || {};
        const neg = c.negative || {};
        return (
          <Box key={cur} sx={{ background: BG_PURPLE_GRADIENT, border: "1px solid #ece4fb", borderRadius: "22px", p: "22px", boxShadow: "0 1px 2px rgba(20,18,30,.04),0 18px 36px -24px rgba(86,40,160,.3)" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={{ width: 34, height: 34, borderRadius: "10px", background: BG_WHITE, border: "1px solid #ece4fb", color: PURPLE_DARK, fontFamily: MONO, fontWeight: 600, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center" }}>{CUR_SYM[cur]}</Box>
                <Box sx={{ fontSize: 14, fontWeight: 700, color: TEXT_BODY }}>{cur}</Box>
              </Box>
              <TrendBadge value={c.trend} />
            </Box>
            <Box sx={{ fontSize: "10.5px", letterSpacing: ".14em", color: TEXT_SUBTLE, fontWeight: 700, mt: "18px" }}>
              {t("financial.netBalance").toUpperCase()}
            </Box>
            <Box sx={{ fontFamily: MONO, fontSize: 31, fontWeight: 600, color: TEXT_DARK, letterSpacing: "-.01em", mt: "5px" }}>{CUR_SYM[cur]}{fmt(c.net)}</Box>
            {c.spark && c.spark.length >= 2 && (
              <Box component="svg" viewBox="0 0 120 32" preserveAspectRatio="none" sx={{ width: "100%", height: 34, mt: "10px", overflow: "visible" }}>
                <path d={sparkPath(c.spark)} fill="none" stroke={PURPLE_LIGHT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </Box>
            )}
            <Box sx={{ display: "flex", gap: "10px", mt: "16px" }}>
              <Box onClick={() => go(cur, "positive")} sx={{ flex: 1, background: SUCCESS_BG, borderRadius: "13px", p: "11px 13px", cursor: "pointer", transition: "filter .15s", "&:hover": { filter: "brightness(.97)" } }}>
                <Box sx={{ fontSize: 11, color: "#5f9b7c", fontWeight: 600 }}>{t("financial.liability")} · {pos.customers || 0}</Box>
                <Box sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 600, color: "#117a4f", mt: "3px" }}>{CUR_SYM[cur]}{fmt(pos.rawSum)}</Box>
              </Box>
              <Box onClick={() => go(cur, "negative")} sx={{ flex: 1, background: ERROR_BG, borderRadius: "13px", p: "11px 13px", cursor: "pointer", transition: "filter .15s", "&:hover": { filter: "brightness(.97)" } }}>
                <Box sx={{ fontSize: 11, color: "#b07478", fontWeight: 600 }}>{t("financial.receivable")} · {neg.customers || 0}</Box>
                <Box sx={{ fontFamily: MONO, fontSize: 16, fontWeight: 600, color: "#c0343a", mt: "3px" }}>{CUR_SYM[cur]}{fmt(neg.rawSum)}</Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function OverviewTab({ data, navigate }) {
  const { t } = useTranslation();
  if (!data) return null;
  return (
    <Box>
      <BranchCashCard branchCash={data.branchCash} branchTotalTHB={data.branchTotalTHB} navigate={navigate} />
      <Box sx={{ mt: "20px" }}>
        <OutstandingCard outstanding={data.outstanding || []} total={data.outstandingTotal} navigate={navigate} />
      </Box>
      <Box sx={{ ...cardLabelSx, fontSize: "11.5px", mt: "28px" }}>{t("financial.customerWallet")}</Box>
      <Box sx={{ mt: "12px" }}>
        <WalletCards wallet={data.wallet} navigate={navigate} />
      </Box>
    </Box>
  );
}

export default function FinancialDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    const res = await getFinancialDashboard();
    setData(res && res.success ? res.data : null);
    setLoading(false);
  }, []);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  return (
    <PageShell title={t("financial.title")}>
      {loading ? <Box sx={{ p: 4 }}><CircularProgress color="secondary" /></Box> : <OverviewTab data={data} navigate={navigate} />}
    </PageShell>
  );
}
